import { TextButton } from '@creditornot/cb-button';
import { useMutation } from '@tanstack/react-query';
import React, { useCallback } from 'react';
import toast from 'react-hot-toast';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { bulkEditItems } from 'apis/restaurant-api/merchant-admin/menu2/api';
import { BulkEditInventoryModePayload, Category } from 'apis/restaurant-api/merchant-admin/menu2/types';
import useMenuData from 'modules/menu/useMenuData';

import { buildBulkActionBasePayload } from '../../utils';

const UndoButton = styled(TextButton).attrs({
  variant: 'neutral',
})`
  display: block;
  margin-inline-start: auto;
`;

const SUCCESS_TIMEOUT = 10 * 1000;

const useBulkChangeStockAvailability = () => {
  const { updateMenuData } = useMenuData();

  const mutateStockAvailability = useMutation(
    ({
      categories,
      inventoryMode,
      menuId,
      selectedIds,
    }: {
      menuId: string;
      selectedIds: Set<string>;
      categories: Category[];
      inventoryMode: BulkEditInventoryModePayload;
    }) => {
      return bulkEditItems(menuId, {
        ...buildBulkActionBasePayload(categories, selectedIds),
        inventory_mode: inventoryMode,
      });
    },
  );

  const submit = useCallback(
    async ({
      categories,
      inventoryMode,
      menuId,
      selectedIds,
    }: {
      categories: Category[];
      inventoryMode: BulkEditInventoryModePayload;
      menuId: string;
      selectedIds: Set<string>;
    }) => {
      const result = await mutateStockAvailability.mutateAsync({ categories, inventoryMode, menuId, selectedIds });
      const undo = () =>
        mutateStockAvailability.mutateAsync({
          categories,
          inventoryMode: inventoryMode === 'forced_out_of_stock' ? null : 'forced_out_of_stock',
          menuId,
          selectedIds,
        });

      return {
        result,
        undo,
      };
    },
    [mutateStockAvailability],
  );

  const bulkChangeStockAvailability = useCallback(
    ({
      categories,
      inStockSelectedIds,
      inventoryMode,
      menuId,
      onClearSelection,
      outOfStockSelectedIds,
    }: {
      categories: Category[];
      inStockSelectedIds: string[];
      inventoryMode: BulkEditInventoryModePayload;
      menuId: string;
      onClearSelection?: () => void;
      outOfStockSelectedIds: string[];
    }) => {
      const selectedIds =
        inventoryMode === 'forced_out_of_stock' ? new Set(inStockSelectedIds) : new Set(outOfStockSelectedIds);

      toast.promise(
        submit({ categories, inventoryMode, menuId, selectedIds }),
        {
          loading: <FormattedMessage id="menu.bulkActions.stock.submitting" />,
          success: ({ result: successData, undo }) => {
            updateMenuData({ actionType: 'updateMenuContainer', payload: successData });
            onClearSelection?.();

            const handleUndoClick = () => {
              toast.dismiss();
              toast.promise(undo(), {
                loading: <FormattedMessage id="menu.bulkActions.stock.undoChangesSubmitting" />,
                success: undoSuccessData => {
                  updateMenuData({ actionType: 'updateMenuContainer', payload: undoSuccessData });
                  return <FormattedMessage id="menu.bulkActions.stock.undoSuccessDescription" />;
                },
                error: <FormattedMessage id="menu.bulkActions.stock.undoErrorDescription" />,
              });
            };

            return (
              <>
                {inventoryMode === 'forced_out_of_stock' ? (
                  <FormattedMessage id="menu.bulkActions.stock.markOutOfStockSuccessDescription" />
                ) : (
                  <FormattedMessage id="menu.bulkActions.stock.markInStockSuccessDescription" />
                )}
                <UndoButton onClick={handleUndoClick}>
                  <FormattedMessage id="menu.bulkActions.stock.undoButton" />
                </UndoButton>
              </>
            );
          },
          error: <FormattedMessage id="menu.bulkActions.stock.errorDescription" />,
        },
        {
          success: {
            duration: SUCCESS_TIMEOUT,
          },
          error: {
            duration: Infinity,
          },
        },
      );
    },
    [submit, updateMenuData],
  );

  return bulkChangeStockAvailability;
};

export default useBulkChangeStockAvailability;
